import React from 'react'
import { Box, Skeleton, Typography, styled } from '@mui/material'
import { Avatar } from '@/design-system'
import { ConnectionModel } from '@/types'
import { isValidAvatarUrl } from '@/utils/helper'
import { avatarGradient } from '@/features/connections/utils'

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: 12,
})

const Card = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 'var(--ds-radius-md)',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  cursor: 'pointer',
  transition: 'box-shadow 140ms ease, transform 140ms ease',
  '&:hover': {
    boxShadow: 'var(--ds-shadow-lift)',
    transform: 'translateY(-2px)',
  },
})

const Info = styled(Box)({ minWidth: 0, flex: 1 })

const Name = styled(Typography)({
  fontSize: 13,
  fontWeight: 650,
  color: 'var(--ds-ink)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const Sub = styled(Typography)({
  fontSize: 10.5,
  color: 'var(--ds-ink-subtle)',
  marginTop: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export interface ConnectionsPreviewGridProps {
  connections: ConnectionModel[]
  loading?: boolean
  skeletonCount?: number
  onPersonClick?: (conn: ConnectionModel) => void
}

export function ConnectionsPreviewGrid({
  connections,
  loading,
  skeletonCount = 8,
  onPersonClick,
}: ConnectionsPreviewGridProps) {
  if (loading) {
    return (
      <Grid>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton key={i} variant='rectangular' sx={{ height: 60, borderRadius: 'var(--ds-radius-lg)' }} />
        ))}
      </Grid>
    )
  }

  if (!connections.length) return null

  return (
    <Grid>
      {connections.map((conn) => {
        const fullName = `${conn.firstName} ${conn.lastName}`
        const src = conn.profilePicURL && isValidAvatarUrl(conn.profilePicURL) ? conn.profilePicURL : undefined
        const initial = conn.firstName[0]?.toUpperCase() ?? '?'
        const sub = [conn.profession, conn.location].filter(Boolean).join(' · ')

        return (
          <Card key={conn.userID} onClick={() => onPersonClick?.(conn)} role='button' tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onPersonClick?.(conn)}>
            <Avatar
              src={src}
              sx={{
                width: 40,
                height: 40,
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0,
                background: avatarGradient(fullName),
                color: 'var(--ds-accent-ink)',
              }}
            >
              {initial}
            </Avatar>
            <Info>
              <Name title={fullName}>{fullName}</Name>
              {sub && <Sub title={sub}>{sub}</Sub>}
            </Info>
          </Card>
        )
      })}
    </Grid>
  )
}
